import type {
  FourChanSearchResult,
  RedditSearchResult,
  SearchResult,
} from "#next/utils/interfaces";
import { Box, Tooltip } from "@mui/material";

export const Subtitle = ({
  post,
  truncate,
}: {
  post: SearchResult;
  truncate?: boolean;
}) => {
  const date = new Date(post.timestamp);
  const plainDate = date.toLocaleDateString();
  const dateTime = date.toLocaleString();

  function isRedditPost(post: SearchResult): post is RedditSearchResult {
    return post.platform === "Reddit";
  }

  function is4chanPost(post: SearchResult): post is FourChanSearchResult {
    return post.platform === "4chan";
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Tooltip title={dateTime}>
        <span>
          {plainDate} on {post.platform}
        </span>
      </Tooltip>
      {!truncate && is4chanPost(post) && (
        <FourChanBoard board={post.platformFields.board} />
      )}
      {!truncate && isRedditPost(post) && (
        <RedditSubreddit subreddit={post.platformFields.subreddit} />
      )}
    </Box>
  );
};

function FourChanBoard({ board }: { board: string }) {
  const boardFields = boards[board];
  if (!boardFields) return;
  const { topic, description } = boardFields;

  return (
    <Tooltip
      title={
        <div>
          <div>
            <b>{topic}</b>
          </div>
          {description}
        </div>
      }
      sx={{ pointerEvents: "auto" }}
    >
      <span>/{board}/</span>
    </Tooltip>
  );
}

function RedditSubreddit({ subreddit }: { subreddit: string }) {
  if (!subreddit) return;
  return <span>/r/{subreddit}</span>;
}

interface Board {
  topic: string;
  description: string;
}

// taken from https://wiki.bibanon.org/4chan_Boards
const boards: { [key: string]: Board } = {
  "3": {
    topic: "3DCG",
    description: "...for 3D modelling and imagery.",
  },
  a: {
    topic: "Anime & Manga",
    description:
      "...dedicated to the discussion of Japanese animation and manga.",
  },
  aco: {
    topic: "Adult Cartoons",
    description: "...for posting western-style adult cartoon artwork.",
  },
  adv: {
    topic: "Advice",
    description: "...for giving and receiving advice.",
  },
  an: {
    topic: "Animals & Nature",
    description: "...for posting pictures of animals, pets, and nature.",
  },
  b: {
    topic: "Random",
    description:
      "...is the birthplace of Anonymous, and where people go to discuss random topics and create memes on 4chan.",
  },
  bant: {
    topic: "International/Random",
    description:
      "...is 4chan's international hanging out board, where you can have fun with Anonymous all over the world.",
  },
  biz: {
    topic: "Business & Finance",
    description:
      "...for the discussion of business and finance, and cryptocurrencies such as Bitcoin and Dogecoin.",
  },
  c: {
    topic: "Anime/Cute",
    description: "...for cute and moe anime images.",
  },
  cgl: {
    topic: "Cosplay & EGL",
    description:
      "...for the discussion of cosplay, elegant gothic lolita (EGL), and anime conventions.",
  },
  ck: {
    topic: "Food & Cooking",
    description: "...for food pictures and cooking recipes.",
  },
  cm: {
    topic: "Cute/Male",
    description: "...for posting pictures of cute anime males.",
  },
  co: {
    topic: "Comics & Cartoons",
    description:
      "...dedicated to the discussion of Western cartoons and comics.",
  },
  d: {
    topic: "Hentai/Alternative",
    description: "...for alternative hentai images.",
  },
  diy: {
    topic: "Do-It-Yourself",
    description:
      "...for DIY/do it yourself projects, home improvement, and makers.",
  },
  e: {
    topic: "Ecchi",
    description: "...for suggestive (ecchi) hentai images.",
  },
  f: {
    topic: "Flash",
    description: "...for sharing Adobe Flash files (SWFs).",
  },
  fa: {
    topic: "Fashion",
    description:
      "...for images and discussion relating to fashion and apparel.",
  },
  fit: {
    topic: "Fitness",
    description: "...for weightlifting, health, and fitness.",
  },
  g: {
    topic: "Technology",
    description:
      "...for discussing computer hardware and software, programming, and general technology.",
  },
  gd: {
    topic: "Graphic Design",
    description: "...for graphic design.",
  },
  gif: {
    topic: "Adult GIF",
    description: "...dedicated to animated adult GIFs and WEBMs.",
  },
  h: {
    topic: "Hentai",
    description: "...for adult Japanese anime hentai images.",
  },
  hc: {
    topic: "Hardcore",
    description: "...for the posting of adult hardcore pornography.",
  },
  his: {
    topic: "History & Humanities",
    description: "...for discussing and debating history.",
  },
  hm: {
    topic: "Handsome Men",
    description: "...dedicated to sharing adult images of handsome men.",
  },
  hr: {
    topic: "High Resolution",
    description: "...for the sharing of high resolution images.",
  },
  i: {
    topic: "Oekaki",
    description: "...for drawing and sharing art.",
  },
  ic: {
    topic: "Artwork/Critique",
    description: "...for the discussion and critique of art.",
  },
  int: {
    topic: "International",
    description: "...for the exchange of foreign language and culture.",
  },
  j: {
    topic: "Janitor & Moderator Discussion",
    description: "",
  },
  jp: {
    topic: "Otaku Culture",
    description: "...for discussing Japanese otaku culture.",
  },
  k: {
    topic: "Weapons",
    description:
      "...for discussing all types of weaponry, from military tanks to guns and knives.",
  },
  lgbt: {
    topic: "LGBT",
    description:
      "...for Lesbian-Gay-Bisexual-Transgender-Queer and sexuality discussion.",
  },
  lit: {
    topic: "Literature",
    description: "...for the discussion of books, authors, and literature.",
  },
  m: {
    topic: "Mecha",
    description:
      "...for discussing Japanese mecha robots and anime, like Gundam and Macross.",
  },
  mlp: {
    topic: "Pony",
    description:
      "...dedicated to the discussion of My Little Pony: Friendship is Magic.",
  },
  mu: {
    topic: "Music",
    description: "...for discussing all types of music.",
  },
  n: {
    topic: "Transportation",
    description:
      "...for discussing modes of transportation like trains and bicycles.",
  },
  news: {
    topic: "Current News",
    description: "...for current news.",
  },
  o: {
    topic: "Auto",
    description: "...for discussing cars and motorcycles.",
  },
  out: {
    topic: "Outdoors",
    description:
      "...for discussing survivalist skills and outdoor activities such as hiking.",
  },
  p: {
    topic: "Photography",
    description: "...for sharing and critiquing photos.",
  },
  po: {
    topic: "Papercraft & Origami",
    description:
      "...for posting papercraft and origami templates and instructions.",
  },
  pol: {
    topic: "Politically Incorrect",
    description: "...for discussing and debating politics and current events.",
  },
  pw: {
    topic: "Professional Wrestling",
    description: "...for the discussion of professional wrestling.",
  },
  qst: {
    topic: "Quests",
    description: "...for grinding XP.",
  },
  r: {
    topic: "Adult Requests",
    description: "...dedicated to fulfilling all types of user requests.",
  },
  r9k: {
    topic: "ROBOT9001",
    description: "...for hanging out and posting greentext stories.",
  },
  s: {
    topic: "Sexy Beautiful Women",
    description: "...dedicated to sharing images of softcore pornography.",
  },
  s4s: {
    topic: "Sh*t 4chan Says",
    description: "...for posting dank memes :^)",
  },
  sci: {
    topic: "Science & Math",
    description: "...for the discussion of science and math.",
  },
  soc: {
    topic: "Cams & Meetups",
    description: "...for camwhores and meetups.",
  },
  sp: {
    topic: "Sports",
    description: "...for sports discussion.",
  },
  t: {
    topic: "Torrents",
    description: "...for posting links and descriptions to torrents.",
  },
  test: {
    topic: "Testing",
    description: "",
  },
  tg: {
    topic: "Traditional Games",
    description:
      "...for discussing traditional gaming, such as board games and tabletop RPGs.",
  },
  toy: {
    topic: "Toys",
    description: "...for talking about all kinds of toys!",
  },
  trash: {
    topic: "Off-Topic",
    description: "...4chan's imageboard jail for off-topic threads.",
  },
  trv: {
    topic: "Travel",
    description: "...dedicated to travel and the countries of the world.",
  },
  tv: {
    topic: "Television & Film",
    description: "...dedicated to the discussion of television and film.",
  },
  u: {
    topic: "Yuri",
    description: "...for yuri hentai images.",
  },
  v: {
    topic: "Video Games",
    description:
      "...dedicated to the discussion of PC and console video games.",
  },
  vg: {
    topic: "Video Game Generals",
    description:
      "...dedicated to the discussion of PC and console video games.",
  },
  vip: {
    topic: "Very Important Posts",
    description: "...for Pass users.",
  },

  vm: {
    topic: "Video Games/Multiplayer",
    description: "...dedicated to the discussion of multiplayer video games.",
  },
  vmg: {
    topic: "Video Games/Mobile",
    description:
      "...dedicated to the discussion of video games on mobile devices.",
  },
  vp: {
    topic: "Pokemon",
    description:
      "...dedicated to discussing the Pokemon series of video games and shows.",
  },
  vr: {
    topic: "Retro Games",
    description:
      "...for discussing retro console video games and classic arcade games.",
  },
  vrpg: {
    topic: "Video Games/RPG",
    description: "...dedicated to the discussion of role-playing video games.",
  },
  vst: {
    topic: "Video Games/Strategy",
    description: "...dedicated to the discussion of strategy video games.",
  },
  vt: {
    topic: "Virtual YouTubers",
    description: "...for discussing virtual YouTubers.",
  },
  w: {
    topic: "Anime/Wallpapers",
    description: "...for posting Japanese anime wallpapers.",
  },
  wg: {
    topic: "Wallpapers/General",
    description: "...for posting general wallpapers.",
  },
  wsg: {
    topic: "Worksafe GIF",
    description: "...dedicated to sharing worksafe animated GIFs and WEBMs.",
  },
  wsr: {
    topic: "Worksafe Requests",
    description: "...dedicated to fulfilling non-NSFW requests.",
  },
  x: {
    topic: "Paranormal",
    description:
      "...for the discussion of paranormal, spooky pictures and conspiracy theories.",
  },
  xs: {
    topic: "Extreme Sports",
    description: "...dedicated to the discussion of extreme sports.",
  },
  y: {
    topic: "Yaoi",
    description: "...for posting adult yaoi hentai images.",
  },
};
